import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DbQueryService, UnitOfWork } from '@data';
import { StringFns } from '@utils';
import { EntityError } from 'breeze-client';
import { MenuItem } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HtmlEditorComponent } from '../editor/html-editor.component';
import { HelpService } from './help.service';

export interface HelpDialogData {
  /** Component that called the dialog.  Route is used to determine help content */
  parent: { selector: string; dbQueryService?: DbQueryService };

  /** Name of field on the page, for field-specific content; null/undefined for page content */
  field: string;

  /** Optional anchor ID to scroll to within the help content (e.g., 'information', 'allowances') */
  anchor?: string;

  /** For calling navigateToValidationError to use help page to activate field */
  callback?: (ee: EntityError, event: MouseEvent) => void;

  /** UOW for querying help items.  If not specified, uow is taken from dbQueryService of the parent */
  uow?: UnitOfWork;
}

@Component({
  selector: 'prox-help-dialog',
  template: `
    <!-- Pencil-edit path hidden 2026-08-13: TipTap toolbar cannot produce the polished
         styled HTML that fragment-authored help uses, so saving through the pencil silently
         destroyed all icons/callouts/spacing. Edit-mode plumbing (canEdit/edit/save/
         HelpService.save) is kept intact for possible future reuse behind a safer surface. -->
    <button
      pButton
      *ngIf="!isEdit"
      (click)="close()"
      class="p-button-rounded p-button-text"
      icon="fa-solid fa-xmark"
      style="position: absolute; top: 1.5rem; right: 3rem"
    ></button>
    <div class="flex h-full flex-col">
      <div #contentContainer *ngIf="!isEdit" class="grow overflow-auto" [innerHTML]="content"></div>
      <div *ngIf="isEdit" class="grow">
        <prox-html-editor [suggestItems]="menuItems" [value]="template"></prox-html-editor>
      </div>
      <div *ngIf="isEdit" class="text-right">
        <button (click)="save()" pButton type="button" icon="pi pi-check" label="Save" class="p-button-raised p-button-sm mr-2"></button>
        <button (click)="cancelEdit()" pButton type="button" icon="pi pi-times" label="Cancel" class="p-button-raised p-button-sm"></button>
      </div>
    </div>`,
  standalone: false,
})
export class HelpDialogComponent implements OnInit, AfterViewChecked {
  @ViewChild(HtmlEditorComponent) htmlEditorComponent!: HtmlEditorComponent;
  @ViewChild('contentContainer', { static: false }) contentContainer!: ElementRef<HTMLDivElement>;

  /** Identifier for help content in the database. */
  key: string;
  /** html with placeholder tokens */
  template?: string;
  /** html with values merged */
  content?: SafeHtml;
  data: HelpDialogData;
  isEdit = false;
  menuItems: MenuItem[] = [];

  /** Flag to track if we need to scroll to anchor */
  private pendingAnchorScroll = false;

  constructor(
    dialogConfig: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private domSanitizer: DomSanitizer,
    private helpService: HelpService,
    private cdr: ChangeDetectorRef,
  ) {
    this.data = dialogConfig.data;
    helpService.init(dialogConfig.data.uow || dialogConfig.data.parent.dbQueryService.uow);
    this.key = this.data.parent.selector;
  }

  async ngOnInit(): Promise<void> {
    this.menuItems = await this.helpService.getMenuItems();
    this.showContent();
  }

  ngAfterViewChecked(): void {
    // Scroll to anchor after view is fully rendered
    if (this.pendingAnchorScroll && this.data.anchor && this.contentContainer?.nativeElement) {
      const container = this.contentContainer.nativeElement;
      const element = container.querySelector(`#${this.data.anchor}`);
      // console.log('Help dialog anchor scroll check:', {
      //   anchor: this.data.anchor,
      //   containerExists: !!container,
      //   elementFound: !!element,
      //   containerHTML: container?.innerHTML?.substring(0, 200)
      // });
      if (element) {
        this.pendingAnchorScroll = false;
        // Use requestAnimationFrame to ensure DOM is fully painted
        requestAnimationFrame(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }

  async showContent(): Promise<void> {
    // console.log('Help dialog showContent called, anchor:', this.data.anchor);
    const item = await this.helpService.getItem(this.key, this.data.field);
    this.template = item?.html;
    const content = item?.mergedHtml ? StringFns.purify(item.mergedHtml) : 'no content';
    this.content = this.domSanitizer.bypassSecurityTrustHtml(content);

    // Set flag to scroll to anchor on next view check
    if (this.data.anchor) {
      console.log('Setting pendingAnchorScroll = true for anchor:', this.data.anchor);
      this.pendingAnchorScroll = true;
      this.cdr.detectChanges();
    }
  }

  canEdit() {
    return !this.isEdit && this.helpService.canEdit();
  }

  edit() {
    if (this.isEdit) {
      this.isEdit = false;
    } else {
      this.isEdit = true;
      setTimeout(() => (this.htmlEditorComponent.value = this.template), 1);
    }
  }

  cancelEdit() {
    this.isEdit = false;
  }

  async save() {
    let value = this.htmlEditorComponent?.value;
    if (value) {
      value = StringFns.purify(value);
      await this.helpService.save(this.key, this.data.field, value);
      await this.showContent();
    }
    this.cancelEdit();
  }

  onClick(ee: EntityError, event: MouseEvent) {
    if (this.data.callback) {
      this.data.callback(ee, event);
    }
  }

  close() {
    this.ref.close();
  }
}
